// eslint-disable-next-line no-restricted-imports
import { GQUserUploadForDatasourceFragment } from '../generated/graphql';
import {
  PermissionObjectType,
  PermissionType,
} from '@watershed/constants/permissions';

// eslint-disable-next-line no-restricted-imports
import * as z3 from 'zod/v3';
import { UserUploadForDatasource } from '../measurement/userUploadUtils';
import { ReportQuestionInstanceV0Snapshot } from './reportQuestionInstanceApprovalsTypes';

// An ApprovalTaskTarget is an object that can be pointed at by an approval task.
export const ApprovalTaskTargets = [
  'UserUploadTask',
  'ReportQuestionInstance',
] as const;
export type ApprovalTaskTargetKind = (typeof ApprovalTaskTargets)[number];
export const ApprovalTaskTargetKindSchema = z3.enum(ApprovalTaskTargets);

// An ApprovalEventTarget is an object that can be pointed at by an approval
// event. All approval task targets are also approval event targets.
//
// The reason for this distinction is that sometimes we want to record data
// approval events for things that aren't pointed at by an approval task.
// Instead, we want to use a 'proxy' task target, that holds all the information
// about things like approver assignment.
//
// For example, when we approve a user upload task, we also record approval
// events for each user upload inside of it. Then, in the future when we
// decouple user upload tasks and user uploads, we will be able to resolve the
// approval status of a user upload independently of its user upload task. Here:
// - User upload task: both a task target and an event target.
// - User upload: an event target only, delegates approver assignment to the
//   corresponding user upload task's approval task.
export const ApprovalEventTargets = [
  ...ApprovalTaskTargets,
  'UserUpload',
] as const;
export type ApprovalEventTargetKind = (typeof ApprovalEventTargets)[number];
export const ApprovalEventTargetKindSchema = z3.enum(ApprovalEventTargets);

// The semantics of Approvalstatus depend on whether the status is attached to a
// target or an approver.
//
// For a target, this is its overall approval status.
// - NotReadyForApproval: the target is not submitted for approval. This might
//   be because it has never been submitted, or because it has been unlocked, or
//   because it has been rejected.
// - SubmittedForApproval: the target is submitted for approval.
// - Approved: the target is approved, as a result of aggregating its approver's
//   reviews.
//
// For an approver, this is that approver's review for the target.
// - NotReadyForApproval: the approver is rejecting the target.
// - SubmittedForApproval: the approver's review is requested.
// - Approved: the approver is approving the target.
//
// Currently the same as GQApprovalStatus in GraphQL.
export type ApprovalStatus =
  | 'SubmittedForApproval'
  | 'Approved'
  | 'NotReadyForApproval';

export type ApprovalTargetLockState = 'Unlocked' | 'Locked' | 'UnlockRequested';

export type ApprovalTaskTargetParams = {
  orgId: string;
  id: string;
  kind: ApprovalTaskTargetKind;
};

// TODO(sonjoonho): This typing is a bit clunky. Maybe it would be better if
// ApprovalTaskTargetParams didn't have orgId, and instead this is always passed
// as a function argument or fetched from the context. Then here, and in other
// places where need to operate on batches, we can simply use
// Array<ApprovalTaskTargetParams>.
export type ApprovalTaskTargetParamsBatch = Array<
  Omit<ApprovalTaskTargetParams, 'orgId'>
>;

export type ApprovalEventTargetParams = {
  orgId: string;
  id: string;
  kind: ApprovalEventTargetKind;
};

// This is a combination of the ApprovalState enum and the user who's viewing the UI
export const DataGovApprovalViewState = {
  // 1a. Earliest state ex. no files have been uploaded or they're processing/errored
  CANNOT_SUBMIT: 'CANNOT_SUBMIT',
  // 1b. Cannot submit because you don't have permission
  CANNOT_SUBMIT_NO_PERMISSION: 'CANNOT_SUBMIT_NO_PERMISSION',
  // 2a. No files exist. User can submit empty data as "none to upload"
  CAN_SUBMIT_EMPTY: 'CAN_SUBMIT_EMPTY',
  // 2b. 1+ file exists, none are processing or errored, not yet approved or marked ready to approve
  // TODO: do we ever want to limit who can submit for approval? ie. approver can't or uploader must?
  CAN_SUBMIT: 'CAN_SUBMIT',
  // 3a. Marked ready to approve, but is not the approver
  SUBMITTED_BUT_NO_PERMISSION: 'SUBMITTED_BUT_NO_PERMISSION',
  // 3b. Marked ready to approve, is the approver
  SUBMITTED_HAS_PERMISSION: 'SUBMITTED_HAS_PERMISSION',
  // 3c. Is the approver, awaiting other approvers
  APPROVED_AWAITING_OTHERS: 'APPROVED_AWAITING_OTHERS',
  // TODO: 3??. Marked ready to approve and you were the submitter (just shows you're done, button disabled?)
  // TODO: 3??. If you're price gated out and don't have approvals, tooltip/dialog smth like "If you submit, it will auto-approve"
  // 4. Approved, no action needed
  APPROVED: 'APPROVED',
  // 4a. Approved, but can still reject (ie. if you're the approver)
  APPROVED_CAN_REJECT: 'APPROVED_CAN_REJECT',
  // TODO: do we want to show anything different for data that's been "unapproved"
  // by a step earlier in the chain? leaning no, activity log should handle this

  // These states are for when approvals are disabled for the project
  // 5. Approvals are disabled for the project, and the user is still uploading
  APPROVALS_DISABLED_UPLOADING: 'APPROVALS_DISABLED_UPLOADING',
  // 6. Approval are disabled for the project, and the user has incomplete facilities
  APPROVALS_DISABLED_FACILITIES_INCOMPLETE:
    'APPROVALS_DISABLED_FACILITIES_INCOMPLETE',
  // 7. Approvals are disabled for the project, and the user has uploaded all the data
  APPROVALS_DISABLED_COMPLETE: 'APPROVALS_DISABLED_COMPLETE',
  // TODO: how should data lock affect this?
  // TODO: relatedly, measurement is marked as complete? is it the same state as data locked?
};
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentionally naming the variable the same as the type to mirror enum behavior
export type DataGovApprovalViewState =
  (typeof DataGovApprovalViewState)[keyof typeof DataGovApprovalViewState];

export const DataGovApprovalsButtonType = {
  APPROVAL_DROPDOWN: 'APPROVAL_DROPDOWN',
  POST_APPROVAL_DROPDOWN: 'POST_APPROVAL_DROPDOWN',
  APPROVED: 'APPROVED',
  SUBMIT_FOR_APPROVAL: 'SUBMIT_FOR_APPROVAL',
  APPROVALS_DISABLED_COMPLETE_TASK: 'APPROVALS_DISABLED_COMPLETE_TASK',
  APPROVALS_DISABLED_UNDO: 'APPROVALS_DISABLED_UNDO',
};

export enum DataGovApprovalsLockingButtonType {
  LOCK_TOGGLE = 'LOCK_TOGGLE',
  UNLOCK_REQUEST = 'UNLOCK_REQUEST',
  // When the data has not been approved by anyone yet, instead of requested an unlock, the
  // uploader should be able to directly unlock the data
  UPLOADER_UNLOCK = 'UPLOADER_UNLOCK',
  APPROVALS_DISABLED_REOPEN = 'APPROVALS_DISABLED_REOPEN',
}

export type DataGovDatasourceUploadsApprovalSettings = {
  approvalViewState: string; // TODO: make type of values of obj
  buttonColor?: 'primary' | 'secondary' | undefined;
  buttonType: string; // TODO: make type of values of obj
  isDisabled: boolean;
  calloutText: string;
  nextStepLinks?: Array<{
    label: string;
    href: string;
  }>;
  tooltip?: string;
  variant?: 'text' | undefined;
};

export type DataGovUserPermissions = Array<DataGovUserPermission>;

type DataGovUserPermission = {
  permission: PermissionType;
  objectId?: string | null | undefined;
  objectType?: PermissionObjectType | null | undefined;
};

export type DataApprovalsFiles = Array<DataApprovalsFile>;

export type DataApprovalsFile = UserUploadForDatasource &
  Pick<GQUserUploadForDatasourceFragment, 'status' | 'processingMode'>;

export type ApprovalEmailRecipientType =
  | 'approvers'
  | 'uploaders'
  | 'admins'
  | 'manageMeasurement';

export type DataApprovalEventAction =
  | 'Approved'
  | 'Rejected'
  | 'Submitted'
  | 'UnlockRequested';
export type ApprovalEmailEventAction = DataApprovalEventAction | 'Unlocked';

////////////////////////////////////////////////////////////////////
// Types originating in the service layer
////////////////////////////////////////////////////////////////////

export type ApprovalDataSnapshot =
  | UserUploadTaskDataSnapshot
  | UserUploadSnapshot
  | ReportQuestionInstanceV0Snapshot;

export const USER_UPLOAD_TASK_SNAPSHOT_CURRENT_VERSION = 0;

const userUploadTaskSnapshotVersionField = z3
  .number()
  .int()
  .min(0)
  .max(USER_UPLOAD_TASK_SNAPSHOT_CURRENT_VERSION)
  // We didn't write version numbers early in the project, so missing version means v0.
  // Only Watershed devs and demo/test orgs should have any events missing version numbers.
  .default(0);

// Schema for the dataSnapshot field we store in the data approval event db
export const USER_UPLOAD_TASK_SNAPSHOT_SCHEMA = z3
  .object({
    version: userUploadTaskSnapshotVersionField,
    userUploadIds: z3.array(z3.string()),
  })
  .or(
    z3.object({
      version: userUploadTaskSnapshotVersionField,
      buildings: z3.array(
        z3.object({
          id: z3.string(),
          revision: z3.number().int().nonnegative(),
        })
      ),
    })
  );
export type UserUploadTaskDataSnapshot = z3.infer<
  typeof USER_UPLOAD_TASK_SNAPSHOT_SCHEMA
>;

export const USER_UPLOAD_SNAPSHOT_SCHEMA = z3.object({});
export type UserUploadSnapshot = z3.infer<typeof USER_UPLOAD_SNAPSHOT_SCHEMA>;

export const REJECTION_EVENT_METADATA_SCHEMA = z3.object({
  rejectionFromUnlocking: z3.boolean().optional(),
});

// Utility type to extract the type from the union based on the presence of a property
type ExtractTypeWithProperty<T, K extends string> =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<K, any> ? T : never;

export function isUserUploadSnapshot(
  snapshot: UserUploadTaskDataSnapshot
): snapshot is ExtractTypeWithProperty<
  UserUploadTaskDataSnapshot,
  'userUploadIds'
> {
  return 'userUploadIds' in snapshot;
}

export function isBuildingSnapshot(
  snapshot: UserUploadTaskDataSnapshot
): snapshot is ExtractTypeWithProperty<
  UserUploadTaskDataSnapshot,
  'buildings'
> {
  return 'buildings' in snapshot;
}
